import React from 'react';
import { createPortal } from 'react-dom';
import { DayPicker, useDayRender, type DayProps } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format, parse, isValid } from 'date-fns';
import { tintFromAccent } from './_shared';

// The brand-themed react-day-picker popover, extracted from DateField so any
// trigger (the wizard's labeled field, the report's compact date chips) can
// share the exact same calendar. The caller owns the `open` state and supplies
// the trigger element via `anchorRef`; this component handles positioning,
// outside-click / Escape dismissal, and selection.

interface CalendarPopoverProps {
  open: boolean;
  onClose: () => void;
  /** The trigger element the calendar floats beneath; also excluded from
      outside-click dismissal so clicking the trigger toggles rather than
      double-fires. */
  anchorRef: React.RefObject<HTMLElement>;
  value: string; // YYYY-MM-DD or empty
  onChange: (next: string) => void; // fires with YYYY-MM-DD on selection
  min?: string; // YYYY-MM-DD inclusive
  max?: string;
  minDisabledMessage?: string;
  maxDisabledMessage?: string;
  accentColor: string;
  primaryColor?: string; // deep brand color for caption/nav; defaults to slate-900
}

const CalendarPopover: React.FC<CalendarPopoverProps> = ({
  open, onClose, anchorRef, value, onChange, min, max,
  minDisabledMessage, maxDisabledMessage, accentColor, primaryColor,
}) => {
  const deepColor = primaryColor ?? 'rgb(15, 23, 42)';
  const popoverRef = React.useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = React.useState<{ top: number; left: number } | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (anchorRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, anchorRef, onClose]);

  // Anchor the portal'd popover to the trigger so it floats just below it,
  // escaping any overflow-hidden parents.
  React.useEffect(() => {
    if (!open || !anchorRef.current) return;
    const measure = () => {
      const rect = anchorRef.current!.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 4, left: rect.left + window.scrollX });
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open, anchorRef]);

  const selected = value ? parse(value, 'yyyy-MM-dd', new Date()) : undefined;
  const minDate = min ? parse(min, 'yyyy-MM-dd', new Date()) : undefined;
  const maxDate = max ? parse(max, 'yyyy-MM-dd', new Date()) : undefined;

  // Custom day renderer that adds a native `title` tooltip on out-of-range days
  // explaining why they're disabled. react-day-picker v8's `useDayRender` does
  // the heavy lifting — we just splice a `title` onto the button it produces.
  const DayWithTooltip = (props: DayProps): JSX.Element | null => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const render = useDayRender(props.date, props.displayMonth, buttonRef);
    if (render.isHidden) return <span role="gridcell" />;
    if (!render.isButton) return <div {...render.divProps} />;
    let title: string | undefined;
    const d = new Date(props.date);
    d.setHours(0, 0, 0, 0);
    if (minDate && d < minDate) title = minDisabledMessage;
    else if (maxDate && d > maxDate) title = maxDisabledMessage;
    return <button {...render.buttonProps} title={title} ref={buttonRef} />;
  };

  if (!open || !pos) return null;

  return createPortal(
    <div
      ref={popoverRef}
      className="rounded-2xl bg-white p-2 sleek-day-picker overflow-hidden"
      style={{
        position: 'absolute',
        top: pos.top,
        left: pos.left,
        zIndex: 50,
        ['--rdp-accent-color' as string]: accentColor,
        ['--rdp-background-color' as string]: tintFromAccent(accentColor, 0.12),
        ['--rdp-deep-color' as string]: deepColor,
        border: `1px solid ${tintFromAccent(accentColor, 0.25)}`,
        boxShadow:
          `0 1px 0 rgba(255,255,255,0.8) inset, 0 12px 32px -10px ${tintFromAccent(accentColor, 0.5)}, 0 4px 12px -4px rgba(15,23,42,0.15)`,
      }}
    >
      <span
        aria-hidden
        className="block absolute inset-x-0 top-0 h-px"
        style={{
          background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
          opacity: 0.6,
        }}
      />
      <DayPicker
        mode="single"
        selected={selected}
        onSelect={(d) => {
          if (d && isValid(d)) {
            onChange(format(d, 'yyyy-MM-dd'));
            onClose();
          }
        }}
        defaultMonth={selected ?? minDate ?? new Date()}
        disabled={[
          ...(minDate ? [{ before: minDate }] : []),
          ...(maxDate ? [{ after: maxDate }] : []),
        ]}
        showOutsideDays
        components={{
          CaptionLabel: ({ displayMonth }: { displayMonth: Date }) => (
            <span
              style={{
                color: accentColor,
                fontSize: '13px',
                fontWeight: 600,
                letterSpacing: '-0.01em',
              }}
            >
              {format(displayMonth, 'MMMM yyyy')}
            </span>
          ),
          Day: DayWithTooltip,
        }}
        modifiersStyles={{
          selected: {
            backgroundColor: accentColor,
            color: 'white',
            fontWeight: 600,
            borderRadius: '999px',
            boxShadow: `0 4px 10px -4px ${accentColor}`,
          },
          today: {
            color: accentColor,
            fontWeight: 700,
          },
        }}
      />

      {/* Custom CSS overrides — soften day cells and theme to brand. Uses
          interpolated colors instead of CSS vars because react-day-picker
          re-defines its own --rdp-accent-color on the .rdp root, blocking
          inheritance from the popup wrapper. */}
      <style>{`
        .sleek-day-picker .rdp {
          --rdp-accent-color: ${accentColor} !important;
          --rdp-background-color: ${tintFromAccent(accentColor, 0.12)} !important;
        }`}</style>
      <style>{`
        .sleek-day-picker .rdp { margin: 0; --rdp-cell-size: 38px; }
        .sleek-day-picker .rdp-caption { padding: 8px 8px 4px 8px; }
        .sleek-day-picker .rdp-caption_label,
        .sleek-day-picker .rdp-caption,
        .sleek-day-picker .rdp-caption button {
          color: var(--rdp-deep-color, rgb(15, 23, 42)) !important;
        }
        .sleek-day-picker .rdp-caption_label {
          font-size: 13px;
          font-weight: 600;
          letter-spacing: -0.01em;
        }
        .sleek-day-picker .rdp-nav_button {
          width: 28px; height: 28px;
          border-radius: 999px;
          color: rgb(100, 116, 139);
        }
        .sleek-day-picker .rdp-nav_button:hover {
          background: var(--rdp-background-color);
          color: var(--rdp-deep-color);
        }
        .sleek-day-picker .rdp-head_cell {
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgb(148, 163, 184);
          padding-bottom: 4px;
        }
        .sleek-day-picker .rdp-day {
          border-radius: 999px;
          font-size: 13px;
          font-weight: 500;
          color: rgb(51, 65, 85);
          transition: all 120ms ease;
        }
        .sleek-day-picker .rdp-day:hover:not([disabled]):not(.rdp-day_selected) {
          background: var(--rdp-background-color);
          color: var(--rdp-accent-color);
        }
        .sleek-day-picker .rdp-day_today:not(.rdp-day_selected) {
          color: var(--rdp-accent-color);
          font-weight: 700;
        }
        .sleek-day-picker .rdp-day_today:not(.rdp-day_selected)::after {
          content: '';
          display: block;
          width: 4px; height: 4px;
          background: var(--rdp-accent-color);
          border-radius: 999px;
          margin: -3px auto 0 auto;
        }
        .sleek-day-picker .rdp-day_selected {
          background: var(--rdp-accent-color) !important;
          color: white !important;
          font-weight: 600;
          box-shadow: 0 4px 10px -4px var(--rdp-accent-color);
        }
        .sleek-day-picker .rdp-day_outside {
          color: rgb(203, 213, 225);
        }
        .sleek-day-picker .rdp-day_disabled {
          color: rgb(148, 163, 184) !important;
          opacity: 1 !important;
          text-decoration: line-through;
          text-decoration-color: rgb(203, 213, 225);
          text-decoration-thickness: 1px;
          cursor: not-allowed !important;
        }
        .sleek-day-picker .rdp-day_disabled:hover {
          background: transparent !important;
          color: rgb(148, 163, 184) !important;
        }
      `}</style>
    </div>,
    document.body,
  );
};

export default CalendarPopover;
